import { useHomepageHydration } from "../../context/HomepageRenderContext";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  resolveImageUrl,
  resolvePortalImage,
} from "../../utils/imageVariants";
import { emitPortalImageTelemetry } from "../../utils/imageDeliveryTelemetry";
import {
  getImageWorkRetryDelay,
  hasCompletedImageWork,
  imageWorkController,
  markImageWorkCompleted,
  markImageWorkFailed,
  resolveImageWorkRootMargin,
} from "../../utils/imageWorkController";
import { useImageWorkRollout } from "../../utils/imageWorkRollout";
import { usePortalImageRolloutConfig } from "../../utils/portalImageRollout";
import { usePublicListingImageBudget } from "./PublicListingImageBudget";
import "./SmartImage.css";
import { resolvePromotionSources } from "../../utils/promotionImageAssets";
import { resolveCatalogueAvifs, resolveCatalogueSizes } from "../../utils/catalogueImageAssets";

const VIEWPORT_MEDIA = Object.freeze({
  mobile: "(max-width: 767px)",
  tablet: "(min-width: 768px) and (max-width: 1199px)",
  desktop: "(min-width: 1200px)",
});
const PICTURE_FORMAT_ORDER = ["avif", "webp", "jpg", "jpeg", "png"];
const MIME_BY_FORMAT = Object.freeze({
  avif: "image/avif",
  webp: "image/webp",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
});
const clampDuration = (value, fallback, minimum, maximum) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(maximum, Math.max(minimum, Math.round(parsed)));
};

export const SMART_IMAGE_DECODE_TIMEOUT_MS = clampDuration(
  process.env.REACT_APP_SMART_IMAGE_DECODE_TIMEOUT_MS,
  1200,
  100,
  5000,
);
export const SMART_IMAGE_LOAD_TIMEOUT_MS = clampDuration(
  process.env.REACT_APP_SMART_IMAGE_LOAD_TIMEOUT_MS,
  12000,
  4000,
  30000,
);

export const settleSmartImageDecode = async (
  imageElement,
  timeoutMs = SMART_IMAGE_DECODE_TIMEOUT_MS,
) => {
  const startedAt = Date.now();
  if (typeof imageElement?.decode !== "function") {
    return { outcome: "unsupported", durationMs: 0 };
  }

  let timer;
  const decodeResult = Promise.resolve()
    .then(() => imageElement.decode())
    .then(() => "decoded", () => "failed");
  const timeoutResult = new Promise((resolve) => {
    timer = setTimeout(() => resolve("timeout"), Math.max(1, timeoutMs));
  });
  const outcome = await Promise.race([decodeResult, timeoutResult]);
  clearTimeout(timer);
  return {
    outcome,
    durationMs: Math.max(0, Date.now() - startedAt),
  };
};

const cx = (...values) => values.filter(Boolean).join(" ");

const readViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "desktop";
  if (window.matchMedia(VIEWPORT_MEDIA.mobile).matches) return "mobile";
  if (window.matchMedia(VIEWPORT_MEDIA.tablet).matches) return "tablet";
  return "desktop";
};

const useViewport = () => {
  const hydrating = useHomepageHydration();
  const [viewport, setViewport] = useState(readViewport);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const queries = Object.values(VIEWPORT_MEDIA).map((media) => window.matchMedia(media));
    const update = () => setViewport(readViewport());
    queries.forEach((query) => {
      if (typeof query.addEventListener === "function") query.addEventListener("change", update);
      else query.addListener?.(update);
    });
    return () => queries.forEach((query) => {
      if (typeof query.removeEventListener === "function") query.removeEventListener("change", update);
      else query.removeListener?.(update);
    });
  }, []);

  return hydrating ? "desktop" : viewport;
};

const buildPictureSources = (plans, sizes) =>
  Object.entries(plans).flatMap(([viewport, plan]) => {
    const byFormat = (plan.candidates || []).reduce((result, candidate) => {
      const format = String(candidate.format || "").toLowerCase();
      if (!format || !candidate.url) return result;
      if (!result[format]) result[format] = [];
      result[format].push(candidate);
      return result;
    }, {});

    return PICTURE_FORMAT_ORDER.flatMap((format) => {
      const candidates = (byFormat[format] || [])
        .slice()
        .sort((left, right) => Number(left.width || 0) - Number(right.width || 0));
      if (!candidates.length) return [];
      return [{
        key: `${viewport}-${format}`,
        media: VIEWPORT_MEDIA[viewport],
        type: MIME_BY_FORMAT[format] || "",
        sizes,
        srcSet: candidates
          .map((candidate) => candidate.width ? `${candidate.url} ${candidate.width}w` : candidate.url)
          .join(", "),
      }];
    });
  });

const uniqueUrls = (values = []) => {
  const seen = new Set();
  return values.reduce((result, value) => {
    const url = resolveImageUrl(value, "");
    if (!url || seen.has(url)) return result;
    seen.add(url);
    result.push(url);
    return result;
  }, []);
};

const resolveAvailableSourceIndex = (sources = []) => {
  const availableIndex = sources.findIndex((url) => getImageWorkRetryDelay(url) === 0);
  return availableIndex >= 0 ? availableIndex : 0;
};

export function SmartImage({
  image,
  src,
  surfaceKey = "",
  alt = "",
  fallback = "",
  className = "",
  imageClassName = "",
  pictureClassName = "",
  style = {},
  imageStyle = {},
  sizes = "100vw",
  aspectRatio,
  fit,
  objectPosition = "center",
  fill = false,
  absoluteFill = false,
  intrinsic = false,
  loading,
  priority,
  decoding = "async",
  draggable = false,
  showBlur = true,
  transparentBackground = false,
  failureLabel = "Image unavailable",
  rolloutConfig = null,
  onLoad,
  onError,
  ...imageProps
}) {
  const value = image ?? src;
  const viewport = useViewport();
  const runtimePortalImageRollout = usePortalImageRolloutConfig();
  const effectiveRolloutConfig = rolloutConfig || runtimePortalImageRollout;
  const plans = useMemo(
    () => ({
      mobile: resolvePortalImage(value, { surfaceKey, viewport: "mobile", fallback, rolloutConfig: effectiveRolloutConfig }),
      tablet: resolvePortalImage(value, { surfaceKey, viewport: "tablet", fallback, rolloutConfig: effectiveRolloutConfig }),
      desktop: resolvePortalImage(value, { surfaceKey, viewport: "desktop", fallback, rolloutConfig: effectiveRolloutConfig }),
    }),
    [effectiveRolloutConfig, fallback, surfaceKey, value],
  );
  const activePlan = plans[viewport] || plans.desktop;
  const listingImageBudget = usePublicListingImageBudget();
  const budgetAllocationRef = useRef(null);
  if (!budgetAllocationRef.current && listingImageBudget && priority !== true) {
    budgetAllocationRef.current = listingImageBudget.reserve();
  }
  const budgetAllocation = budgetAllocationRef.current;
  const imageWorkRollout = useImageWorkRollout();
  const isPriority = priority ?? (
    activePlan.priority === "critical" || Boolean(budgetAllocation?.priority)
  );
  // Explicit critical images must never inherit a spent listing budget.
  const effectiveLoading = loading || (priority === true ? "eager" : budgetAllocation?.loading)
    || (isPriority ? "eager" : "lazy");
  const canObserveVisibility = typeof IntersectionObserver !== "undefined";
  const canControlVisibility = imageWorkRollout.controlEnabled
    && canObserveVisibility;
  const sourceChainKey = uniqueUrls([
    activePlan.url,
    ...(activePlan.fallbackUrls || []),
    activePlan.fallbackUrl,
    ...(activePlan.legacyFallbackAllowed ? [fallback] : []),
  ]).join("\n");
  // Parent projections and runtime-config hydration may rebuild equivalent
  // entity objects. Keep source-chain identity tied to the actual URLs so an
  // unrelated rerender cannot cancel and requeue admitted image work.
  const sourceChain = useMemo(
    () => sourceChainKey ? sourceChainKey.split("\n") : [],
    [sourceChainKey],
  );
  const preparedSources = useMemo(() => Object.entries(plans).flatMap(([role, plan]) => {
    const promotion = resolvePromotionSources(plan.url, surfaceKey);
    const candidates = promotion.length ? promotion
      : plan.rollout?.optimizedEnabled ? resolveCatalogueAvifs(plan.url, surfaceKey) : [];
    return candidates.length ? [{
      key: `${role}-prepared-avif`, media: VIEWPORT_MEDIA[role], type: "image/avif",
      sizes: promotion.length || (fit || plan.fit) === "contain" ? sizes : resolveCatalogueSizes(plan.url, sizes, aspectRatio || plan.aspectRatio),
      candidateUrls: candidates.map(candidate => candidate.url),
      srcSet: candidates.map(candidate => candidate.width ? `${candidate.url} ${candidate.width}w` : candidate.url).join(", "),
    }] : [];
  }), [plans, sizes, surfaceKey, aspectRatio, fit]);
  const pictureSources = useMemo(() => [...preparedSources, ...buildPictureSources(plans, sizes)], [plans, sizes, preparedSources]);
  const initialSourceIndex = resolveAvailableSourceIndex(sourceChain);
  const wrapperRef = useRef(null);
  const imageRef = useRef(null);
  const activePlanRef = useRef(activePlan);
  const reportedIntegrityRef = useRef("");
  const releaseWorkRef = useRef(null);
  const attemptRef = useRef(0);
  const completedAttemptRef = useRef(-1);
  const workQueuedAtRef = useRef(0);
  const workStartedAtRef = useRef(0);
  const [pictureEnabled, setPictureEnabled] = useState(true);
  const [sourceIndex, setSourceIndex] = useState(initialSourceIndex);
  const currentSource = sourceChain[sourceIndex] || "";
  const [loaded, setLoaded] = useState(() => hasCompletedImageWork(currentSource));
  const [failed, setFailed] = useState(false);
  const [blurFailed, setBlurFailed] = useState(false);
  const [nearViewport, setNearViewport] = useState(
    () => !canObserveVisibility || isPriority || effectiveLoading === "eager" || hasCompletedImageWork(currentSource),
  );
  const [workAllowed, setWorkAllowed] = useState(
    () => !canControlVisibility || isPriority || hasCompletedImageWork(currentSource),
  );
  activePlanRef.current = activePlan;

  useEffect(() => {
    if (!activePlan.missingVariant) return;
    const reportKey = `${activePlan.surfaceKey}:${activePlan.viewport}:${activePlan.slotKey}:${activePlan.presetKey}`;
    if (reportedIntegrityRef.current === reportKey) return;
    reportedIntegrityRef.current = reportKey;
    emitPortalImageTelemetry("image_delivery_contract_missing", activePlan, {
      rejectedReasons: Array.from(new Set(
        (activePlan.rejectedCandidates || []).flatMap((candidate) => candidate.reasons || []),
      )),
    });
  }, [activePlan]);

  const releaseImageWork = useCallback(() => {
    const release = releaseWorkRef.current;
    releaseWorkRef.current = null;
    release?.();
  }, []);

  useEffect(() => {
    const nextSourceIndex = resolveAvailableSourceIndex(sourceChain);
    const nextSource = sourceChain[nextSourceIndex] || "";
    const completed = hasCompletedImageWork(nextSource);
    attemptRef.current += 1;
    completedAttemptRef.current = -1;
    releaseImageWork();
    setPictureEnabled(true);
    setSourceIndex(nextSourceIndex);
    setLoaded(completed);
    setFailed(false);
    setBlurFailed(false);
    setWorkAllowed(!canControlVisibility || isPriority || completed);
  }, [activePlan.slotKey, activePlan.url, activePlan.fallbackUrl, canControlVisibility, isPriority, releaseImageWork, sourceChain, viewport]);

  useEffect(() => {
    if (!currentSource || failed) return undefined;
    if (!canObserveVisibility) {
      setNearViewport(true);
      return undefined;
    }
    if (isPriority || effectiveLoading === "eager" || hasCompletedImageWork(currentSource)) {
      setNearViewport(true);
      return undefined;
    }
    setNearViewport(false);
    const node = wrapperRef.current;
    if (!node) {
      setNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) return;
        setNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: resolveImageWorkRootMargin(), threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [canObserveVisibility, currentSource, effectiveLoading, failed, isPriority]);

  useEffect(() => {
    releaseImageWork();
    if (!currentSource || failed) {
      setWorkAllowed(false);
      return undefined;
    }
    if (!canControlVisibility) {
      setWorkAllowed(true);
      return undefined;
    }
    if (isPriority || hasCompletedImageWork(currentSource)) {
      setWorkAllowed(true);
      return undefined;
    }
    if (!nearViewport) {
      setWorkAllowed(false);
      return undefined;
    }

    let cancelled = false;
    setWorkAllowed(false);
    workQueuedAtRef.current = Date.now();
    const ticket = imageWorkController.request(currentSource, {
      priority: effectiveLoading === "eager",
    });
    // Promote a waiting card as soon as it actually enters view. Keep the same
    // ticket so a priority change never cancels a download already in progress.
    const visibleObserver = new IntersectionObserver((entries) => {
      ticket.setPriority(effectiveLoading === "eager" || entries.some((entry) => entry.isIntersecting));
    }, { rootMargin: "0px", threshold: 0.01 });
    if (wrapperRef.current) visibleObserver.observe(wrapperRef.current);
    ticket.promise.then((release) => {
      if (cancelled) {
        release();
        return;
      }
      releaseWorkRef.current = release;
      workStartedAtRef.current = Date.now();
      if (imageWorkRollout.monitoringEnabled) {
        emitPortalImageTelemetry("image_delivery_work_started", activePlanRef.current, {
          queueWaitMs: Math.max(0, workStartedAtRef.current - workQueuedAtRef.current),
          requestedPriority: effectiveLoading === "eager" ? "eager" : "standard",
          controlled: true,
          controlStage: imageWorkRollout.rolloutStage,
          controlReason: imageWorkRollout.reason,
        });
      }
      setWorkAllowed(true);
    });
    return () => {
      cancelled = true;
      visibleObserver.disconnect();
      ticket.cancel();
      releaseImageWork();
    };
  }, [canControlVisibility, currentSource, effectiveLoading, failed, imageWorkRollout.monitoringEnabled, imageWorkRollout.reason, imageWorkRollout.rolloutStage, isPriority, nearViewport, releaseImageWork]);

  useEffect(() => () => {
    attemptRef.current += 1;
    completedAttemptRef.current = -1;
    releaseImageWork();
  }, [releaseImageWork]);

  const declaredCandidateUrls = useMemo(
    () => new Set(uniqueUrls([
      ...Object.values(plans).flatMap((plan) => (plan.candidates || []).map((candidate) => candidate.url)),
      ...preparedSources.flatMap((source) => source.candidateUrls.map(url =>
        typeof window === "undefined" ? url : new URL(url, window.location.origin).href)),
    ])),
    [plans, preparedSources],
  );

  const isExpectedRenderedSource = useCallback((renderedSource) => {
    if (!renderedSource || renderedSource === currentSource) return true;
    return pictureEnabled && declaredCandidateUrls.has(renderedSource);
  }, [currentSource, declaredCandidateUrls, pictureEnabled]);

  const completeImageLoad = useCallback((imageElement, event, completionSource = "load-event") => {
    const attempt = attemptRef.current;
    const renderedSource = resolveImageUrl(imageElement?.currentSrc || imageElement?.src, "");
    if (!isExpectedRenderedSource(renderedSource)) return false;
    if (completedAttemptRef.current === attempt) return false;
    completedAttemptRef.current = attempt;
    const completedAt = Date.now();
    const plan = activePlanRef.current;
    markImageWorkCompleted(currentSource);
    markImageWorkCompleted(renderedSource);
    releaseImageWork();
    setLoaded(true);
    setFailed(false);
    onLoad?.(event || {
      type: "load",
      target: imageElement,
      currentTarget: imageElement,
    }, plan);

    const controlledWorkMs = workStartedAtRef.current
      ? Math.max(0, completedAt - workStartedAtRef.current)
      : 0;
    void settleSmartImageDecode(imageElement).then((decode) => {
      if (imageWorkRollout.monitoringEnabled) {
        emitPortalImageTelemetry("image_delivery_loaded", plan, {
          fallbackUsed: sourceIndex > 0,
          attempt: sourceIndex + 1,
          completionSource,
          decodeOutcome: decode.outcome,
          decodeDurationMs: decode.durationMs,
          controlledWorkMs,
          controlled: imageWorkRollout.controlEnabled,
          controlStage: imageWorkRollout.rolloutStage,
          controlReason: imageWorkRollout.reason,
        });
      }
    });
    return true;
  }, [currentSource, imageWorkRollout.controlEnabled, imageWorkRollout.monitoringEnabled, imageWorkRollout.reason, imageWorkRollout.rolloutStage, isExpectedRenderedSource, onLoad, releaseImageWork, sourceIndex]);

  const handleLoad = useCallback((event) => {
    completeImageLoad(event.currentTarget, event, "load-event");
  }, [completeImageLoad]);

  const failImageAttempt = useCallback((event, failureReason = "source-error") => {
    const imageElement = event?.currentTarget || imageRef.current;
    const renderedSource = resolveImageUrl(imageElement?.currentSrc || imageElement?.src, "");
    if (!isExpectedRenderedSource(renderedSource)) return;
    const hasNextSource = sourceIndex + 1 < sourceChain.length;
    const willRetryWithoutPicture = pictureEnabled && pictureSources.length > 0 && renderedSource !== currentSource;
    if (imageWorkRollout.monitoringEnabled) {
      if (failureReason === "load-timeout") {
        emitPortalImageTelemetry("image_delivery_stuck", activePlan, {
          attempt: sourceIndex + 1,
          failureReason,
          controlled: imageWorkRollout.controlEnabled,
          controlStage: imageWorkRollout.rolloutStage,
          controlReason: imageWorkRollout.reason,
        });
      }
      emitPortalImageTelemetry("image_delivery_source_error", activePlan, {
        attempt: sourceIndex + 1,
        failureReason,
        willFallback: hasNextSource || willRetryWithoutPicture,
        controlled: imageWorkRollout.controlEnabled,
        controlStage: imageWorkRollout.rolloutStage,
        controlReason: imageWorkRollout.reason,
      });
    }
    markImageWorkFailed(renderedSource || currentSource);
    attemptRef.current += 1;
    completedAttemptRef.current = -1;
    if (pictureEnabled) {
      setPictureEnabled(false);
      setLoaded(false);
      if (!currentSource || renderedSource === currentSource) {
        const nextIndex = sourceIndex + 1;
        if (nextIndex < sourceChain.length) {
          setSourceIndex(nextIndex);
          return;
        }
      } else {
        return;
      }
    } else {
      const nextIndex = sourceIndex + 1;
      if (nextIndex < sourceChain.length) {
        setSourceIndex(nextIndex);
        setLoaded(false);
        return;
      }
    }
    releaseImageWork();
    setWorkAllowed(false);
    setFailed(true);
    setLoaded(false);
    if (imageWorkRollout.monitoringEnabled) {
      emitPortalImageTelemetry("image_delivery_failed", activePlan, {
        attempts: sourceChain.length,
        failureReason,
        controlled: imageWorkRollout.controlEnabled,
        controlStage: imageWorkRollout.rolloutStage,
        controlReason: imageWorkRollout.reason,
      });
    }
    onError?.(event || {
      type: failureReason,
      target: imageElement,
      currentTarget: imageElement,
    }, activePlan);
  }, [activePlan, currentSource, imageWorkRollout.controlEnabled, imageWorkRollout.monitoringEnabled, imageWorkRollout.reason, imageWorkRollout.rolloutStage, isExpectedRenderedSource, onError, pictureEnabled, pictureSources.length, releaseImageWork, sourceChain.length, sourceIndex]);

  const handleError = useCallback((event) => {
    failImageAttempt(event, "source-error");
  }, [failImageAttempt]);

  useEffect(() => {
    if (!currentSource || !workAllowed || loaded || failed) return undefined;
    const imageElement = imageRef.current;
    if (imageElement?.complete) {
      if (Number(imageElement.naturalWidth || 0) > 0) completeImageLoad(imageElement, null, "browser-cache");
      // A server-rendered image can fail before React attaches its handlers.
      // Detect that completed failure now instead of leaving the blur/blank
      // visible until the normal stalled-request timeout expires.
      else failImageAttempt(null, "completed-source-error");
    }
    return undefined;
  }, [completeImageLoad, currentSource, failImageAttempt, failed, loaded, pictureEnabled, sourceIndex, workAllowed]);

  useEffect(() => {
    const canBoundThisAttempt = effectiveLoading === "eager"
      || (canObserveVisibility && nearViewport);
    if (!canBoundThisAttempt || !currentSource || !workAllowed || loaded || failed) return undefined;
    const attempt = attemptRef.current;
    const timer = setTimeout(() => {
      if (attempt !== attemptRef.current || completedAttemptRef.current === attempt) return;
      failImageAttempt(null, "load-timeout");
    }, SMART_IMAGE_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [canObserveVisibility, currentSource, effectiveLoading, failImageAttempt, failed, loaded, nearViewport, pictureEnabled, sourceIndex, workAllowed]);

  const mobileRatio = aspectRatio ?? plans.mobile.aspectRatio ?? "auto";
  const tabletRatio = aspectRatio ?? plans.tablet.aspectRatio ?? mobileRatio;
  const desktopRatio = aspectRatio ?? plans.desktop.aspectRatio ?? tabletRatio;
  const containerStyle = {
    "--smart-image-mobile-ratio": mobileRatio || "auto",
    "--smart-image-tablet-ratio": tabletRatio || "auto",
    "--smart-image-desktop-ratio": desktopRatio || "auto",
    "--smart-image-fit": fit || activePlan.fit || "cover",
    "--smart-image-position": objectPosition,
    ...style,
    ...(absoluteFill
      ? {
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          aspectRatio: "auto",
        }
      : {}),
  };
  const blurUrl = showBlur && !blurFailed ? activePlan.blurUrl : "";
  const terminalFailed = failed || !currentSource;
  const workState = terminalFailed
    ? "failed"
    : loaded
    ? "complete"
    : workAllowed
      ? "active"
      : nearViewport
        ? "queued"
        : "deferred";

  return (
    <span
      ref={wrapperRef}
      className={cx(
        "smart-image",
        transparentBackground && "smart-image--transparent",
        className,
      )}
      style={containerStyle}
      data-first-paint={surfaceKey === "promotion.page-banner" && isPriority ? "true" : undefined}
      data-fill={fill ? "true" : "false"}
      data-absolute-fill={absoluteFill ? "true" : "false"}
      data-intrinsic={intrinsic ? "true" : "false"}
      data-loaded={loaded ? "true" : "false"}
      data-failed={terminalFailed ? "true" : "false"}
      data-source={activePlan.source}
      data-migration={activePlan.migrationState}
      data-integrity={activePlan.integrityStatus}
      data-missing-variant={activePlan.missingVariant ? "true" : "false"}
      data-rollout={activePlan.rollout?.reason}
      data-slot={activePlan.slotKey || undefined}
      data-image-work={workState}
      data-image-work-rollout={imageWorkRollout.rolloutStage}
      data-image-work-control={imageWorkRollout.reason}
      data-image-budget-family={budgetAllocation?.family || undefined}
      data-image-budget-index={budgetAllocation ? budgetAllocation.index : undefined}
      aria-busy={!loaded && !terminalFailed}
    >
      {blurUrl && (nearViewport || workAllowed) && !terminalFailed ? (
        <img
          src={blurUrl}
          alt=""
          aria-hidden="true"
          className="smart-image__blur"
          loading="eager"
          decoding="async"
          fetchpriority="low"
          draggable={false}
          onError={() => setBlurFailed(true)}
        />
      ) : null}
      {currentSource && !failed && workAllowed ? (
        <picture className={cx("smart-image__picture", pictureClassName)}>
          {pictureEnabled
            ? pictureSources.map((source) => (
                <source
                  key={source.key}
                  media={source.media}
                  type={source.type}
                  srcSet={source.srcSet}
                  sizes={source.sizes}
                />
              ))
            : null}
          <img
            {...imageProps}
            ref={imageRef}
            src={currentSource}
            alt={alt}
            width={activePlan.width || undefined}
            height={activePlan.height || undefined}
            sizes={sizes}
            loading={canControlVisibility ? "eager" : effectiveLoading}
            decoding={decoding}
            fetchpriority={isPriority ? "high" : "auto"}
            draggable={draggable}
            className={cx("smart-image__media", imageClassName)}
            style={imageStyle}
            onLoad={handleLoad}
            onError={handleError}
          />
        </picture>
      ) : null}
      {terminalFailed ? (
        <span
          className="smart-image__fallback"
          role={alt ? "img" : undefined}
          aria-label={alt ? `${alt}: ${failureLabel}` : undefined}
          aria-hidden={alt ? undefined : "true"}
        >
          <i className="fa-regular fa-image" aria-hidden="true" />
          <span>{failureLabel}</span>
        </span>
      ) : null}
    </span>
  );
}

export default React.memo(SmartImage);
